import { clamp, preventDefault, isMobileBreakpoint } from './utilities.js';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const DEFAULT_ZOOM = 1;
const DOUBLE_TAP_MS = 300;
const TAP_MOVE_TOLERANCE = 10;
const DOUBLE_TAP_ZOOM = 2;

export class DragZoomWrapper extends HTMLElement {
  #controller = new AbortController();
  #scale = DEFAULT_ZOOM;
  #initialDistance = 0;
  #startScale = DEFAULT_ZOOM;
  #translate = { x: 0, y: 0 };
  #startPosition = { x: 0, y: 0 };
  #startTranslate = { x: 0, y: 0 };
  #isDragging = false;
  #initialized = false;
  #animationFrame = null;

  #lastTapTime = 0;
  #touchStartPoint = null;
  #moved = false;
  #multiTouch = false;
  #singleTapTimer = null;
  #suppressClickUntil = 0;
  #animTimer = null;

  get #image() {
    return this.querySelector('img');
  }

  connectedCallback() {
    if (!this.#image) return;
    this.#initResizeListener();
    if (!isMobileBreakpoint()) return;
    this.#initEventListeners();
    this.#updateTransform();
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;
    const { signal } = this.#controller;
    const options = { passive: false, signal };

    this.addEventListener('touchstart', this.#handleTouchStart, options);
    this.addEventListener('touchmove', this.#handleTouchMove, options);
    this.addEventListener('touchend', this.#handleTouchEnd, options);
    this.addEventListener('click', this.#handleClickSuppress, { signal });
    this.#image?.addEventListener('load', this.#updateTransform, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
    this.#clearSingleTapTimer();
  }

  #handleResize = () => {
    if (!this.#initialized && isMobileBreakpoint()) this.#initEventListeners();
    if (this.#initialized) this.#requestUpdateTransform();
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  #isAtMinZoom() {
    const minZoom = this.#getMinZoom() ?? MIN_ZOOM;
    return this.#scale <= minZoom + 0.01;
  }

  #handleTouchStart = (event) => {
    // Finger-Gesten immer sofort (kein Ease)
    if (this.#image) this.#image.style.transition = '';
    window.clearTimeout(this.#animTimer);

    if (event.touches.length === 2) {
      preventDefault(event);
      const [point1, point2] = Array.from(event.touches).map(touchToPoint);
      if (!point1 || !point2) return;
      this.#multiTouch = true;
      this.#startZoomGesture(point1, point2);
    } else if (event.touches.length === 1) {
      const point = touchToPoint(event.touches[0]);
      if (!point) return;
      this.#multiTouch = false;
      this.#moved = false;
      this.#touchStartPoint = point;

      if (!this.#isAtMinZoom()) {
        // Reingezoomt -> wir verschieben das Bild selbst
        preventDefault(event);
        this.#startDragGesture(point);
      }
      // Min-Zoom: NICHT preventDefault -> natives Wischen zum nächsten Bild bleibt möglich
    }
  };

  #startZoomGesture(point1, point2) {
    this.#initialDistance = getDistance(point1, point2);
    this.#startScale = this.#scale;
    this.#isDragging = false;
  }

  #startDragGesture(point) {
    this.#startPosition = { x: point.x, y: point.y };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
  }

  #handleTouchMove = (event) => {
    if (event.touches.length === 2) {
      preventDefault(event);
      this.#processZoomGesture(event);
      return;
    }

    if (event.touches.length === 1) {
      const point = touchToPoint(event.touches[0]);
      if (point && this.#touchStartPoint) {
        const dx = point.x - this.#touchStartPoint.x;
        const dy = point.y - this.#touchStartPoint.y;
        if (Math.sqrt(dx * dx + dy * dy) > TAP_MOVE_TOLERANCE) this.#moved = true;
      }
      if (this.#isDragging) {
        preventDefault(event);
        this.#processDragGesture(event);
      }
    }
  };

  #processZoomGesture(event) {
    const [point1, point2] = Array.from(event.touches).map(touchToPoint);
    if (!point1 || !point2) return;

    const currentMidpoint = getMidpoint(point1, point2);
    const currentDistance = getDistance(point1, point2);
    const oldScale = this.#scale;

    const newScale = (currentDistance / this.#initialDistance) * this.#startScale;
    this.#scale = clamp(newScale, MIN_ZOOM, MAX_ZOOM);

    const containerCenter = { x: this.clientWidth / 2, y: this.clientHeight / 2 };
    const distanceFromCenter = {
      x: currentMidpoint.x - containerCenter.x,
      y: currentMidpoint.y - containerCenter.y,
    };
    const scaleDelta = this.#scale / oldScale - 1.0;
    this.#translate.x -= (distanceFromCenter.x * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenter.y * scaleDelta) / this.#scale;

    this.#requestUpdateTransform();
    this.#isDragging = false;
  }

  #processDragGesture(event) {
    const point = touchToPoint(event.touches[0]);
    if (!point) return;
    this.#translate = {
      x: this.#startTranslate.x + (point.x - this.#startPosition.x) / this.#scale,
      y: this.#startTranslate.y + (point.y - this.#startPosition.y) / this.#scale,
    };
    this.#requestUpdateTransform();
  }

  #handleTouchEnd = (event) => {
    if (event.touches.length > 0) return;

    this.#isDragging = false;

    // Geste (Pinch / Verschieben / Wischen)? -> kein Tap
    if (this.#multiTouch || this.#moved) {
      this.#requestUpdateTransform();
      this.#lastTapTime = 0;
      this.#multiTouch = false;
      return;
    }

    // Echter Tap
    const nowT = Date.now();
    this.#suppressClickUntil = nowT + 400;
    preventDefault(event);

    if (nowT - this.#lastTapTime < DOUBLE_TAP_MS) {
      // Doppel-Tipp -> Zoom umschalten (rein ODER raus)
      this.#clearSingleTapTimer();
      this.#lastTapTime = 0;
      this.#toggleZoom(this.#touchStartPoint);
    } else {
      this.#lastTapTime = nowT;
      if (this.#isAtMinZoom()) {
        this.#clearSingleTapTimer();
        this.#singleTapTimer = window.setTimeout(() => {
          this.#lastTapTime = 0;
          this.#close();
        }, DOUBLE_TAP_MS);
      }
    }
  };

  #toggleZoom(point) {
    const minZoom = this.#getMinZoom() ?? MIN_ZOOM;
    this.#animateTransform(); // sanftes Ease für den programmatischen Zoom

    if (this.#isAtMinZoom()) {
      const oldScale = this.#scale;
      this.#scale = clamp(DOUBLE_TAP_ZOOM, minZoom, MAX_ZOOM);
      if (point) {
        const rect = this.getBoundingClientRect();
        const dx = point.x - rect.left - rect.width / 2;
        const dy = point.y - rect.top - rect.height / 2;
        const scaleDelta = this.#scale / oldScale - 1.0;
        this.#translate.x -= (dx * scaleDelta) / this.#scale;
        this.#translate.y -= (dy * scaleDelta) / this.#scale;
      }
    } else {
      this.#scale = minZoom;
      this.#translate = { x: 0, y: 0 };
    }
    this.#requestUpdateTransform();
  }

  #animateTransform() {
    const img = this.#image;
    if (!img) return;
    img.style.transition = 'transform 0.25s ease';
    window.clearTimeout(this.#animTimer);
    this.#animTimer = window.setTimeout(() => {
      if (this.#image) this.#image.style.transition = '';
    }, 280);
  }

  #close() {
    const dialog = this.closest('dialog');
    const closeButton = dialog?.querySelector('.dialog-zoomed-gallery__close-button');
    if (closeButton instanceof HTMLElement) closeButton.click();
  }

  #clearSingleTapTimer() {
    if (this.#singleTapTimer) {
      clearTimeout(this.#singleTapTimer);
      this.#singleTapTimer = null;
    }
  }

  #handleClickSuppress = (event) => {
    if (Date.now() < this.#suppressClickUntil) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
  };

  #getMinZoom = () => {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    const imageDimensions = this.#getImageDimensions();
    if (!imageDimensions || !containerWidth || !containerHeight) return null;
    const { width: imageWidth, height: imageHeight } = imageDimensions;
    // Bild komplett einpassen (statt füllen)
    return Math.min(containerWidth / imageWidth, containerHeight / imageHeight);
  };

  #getImageDimensions = () => {
    const containerRect = this.getBoundingClientRect();
    if (!this.#image) return null;
    const { naturalWidth, naturalHeight } = this.#image;
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    if (!naturalWidth || !naturalHeight || !containerWidth || !containerHeight) return null;

    const containerAspect = containerRect.width / containerRect.height;
    const naturalAspect = naturalWidth / naturalHeight;

    let imageWidth, imageHeight;
    if (naturalAspect > containerAspect) {
      imageWidth = containerRect.width;
      imageHeight = imageWidth / naturalAspect;
    } else {
      imageHeight = containerRect.height;
      imageWidth = imageHeight * naturalAspect;
    }
    if (imageWidth === 0 || imageHeight === 0) return null;
    return { width: imageWidth, height: imageHeight };
  };

  #constrainTranslation() {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    if (!containerWidth || !containerHeight) return;

    const minZoom = this.#getMinZoom();
    if (!minZoom) return;
    this.#scale = clamp(this.#scale, minZoom, MAX_ZOOM);

    const imageDimensions = this.#getImageDimensions();
    if (!imageDimensions) return;
    const { width: imageWidth, height: imageHeight } = imageDimensions;

    const scaledWidth = imageWidth * this.#scale;
    const scaledHeight = imageHeight * this.#scale;
    const excessWidth = Math.max(0, scaledWidth - containerWidth);
    const excessHeight = Math.max(0, scaledHeight - containerHeight);
    const maxTranslateX = excessWidth / (2 * this.#scale);
    const maxTranslateY = excessHeight / (2 * this.#scale);

    this.#translate.x = clamp(this.#translate.x, -maxTranslateX, maxTranslateX);
    this.#translate.y = clamp(this.#translate.y, -maxTranslateY, maxTranslateY);
  }

  #requestUpdateTransform = () => {
    if (!this.#animationFrame) this.#animationFrame = requestAnimationFrame(this.#updateTransform);
  };

  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;
    this.#constrainTranslation();
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
    // Ausgezoomt: nur horizontal wischen (Bildwechsel), kein Seiten-Scroll.
    // Reingezoomt: wir steuern alles -> kein Seiten-Scroll, kein Bildwechsel.
    this.style.touchAction = this.#isAtMinZoom() ? 'pan-x' : 'none';
  };

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
    this.#clearSingleTapTimer();
  }
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

function touchToPoint(touch) {
  if (!touch) return undefined;
  return { x: touch.clientX, y: touch.clientY };
}
function getDistance(point1, point2) {
  const dx = point1.x - point2.x;
  const dy = point1.y - point2.y;
  return Math.sqrt(dx * dx + dy * dy);
}
function getMidpoint(point1, point2) {
  return { x: (point1.x + point2.x) / 2, y: (point1.y + point2.y) / 2 };
}